import React, { useReducer } from 'react';
import Badge from 'components/base/Badge';

type BadgeBg =
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'primary'
  | 'secondary';

interface BadgeState {
  bg: BadgeBg | undefined;
  statusText: string | undefined;
}

type Action = { type: string | number | undefined };

const initialState: BadgeState = { bg: undefined, statusText: undefined };

const badgeReducer = (state: BadgeState, action: Action): BadgeState => {
  switch (action.type) {
    case 'New':
      return { bg: 'success', statusText: 'New' };
    case 'ForBarcoding':
      return { bg: 'warning', statusText: 'For Barcoding' };
    case 'Processed':
    case 'Submitted':
    case 'Paid':
    case 'Active':
      return { bg: 'info', statusText: String(action.type) };
    case 'Unpaid':
    case 'Inactive':
      return { bg: 'warning', statusText: String(action.type) };
    case 'Issued':
      return { bg: 'secondary', statusText: String(action.type) };
    case 'InProgress':
    case 'In Progress':
      return { bg: 'warning', statusText: 'In Progress' };
    case 'Delete Requested':
      return { bg: 'danger', statusText: 'Delete Requested' };
    case 'For Approval':
    case 'ForApproval':
    case 'AwaitingApproval':
      return { bg: 'primary', statusText: 'For Approval' };
    case 'Analyzing':
      return { bg: 'warning', statusText: 'Analyzing' };
    case 'Approved':
      return { bg: 'success', statusText: 'Approved' };
    case 'Rejected':
      return { bg: 'danger', statusText: 'Rejected' };
    case 'PendingOCR':
    case 'Pending OCR':
      return { bg: 'warning', statusText: 'Pending OCR' };
    case 'ReturnedToSupplier':
      return { bg: 'warning', statusText: 'Returned' };
    case 'AwaitingCheckIssuance':
      return { bg: 'warning', statusText: 'Awaiting Check Issuance' };
    case 'AwaitingBankTransfer':
      return { bg: 'warning', statusText: 'Awaiting Bank Transfer' };
    case 'CheckReady':
      return { bg: 'warning', statusText: 'Check Ready' };
    case 'CheckReleased':
      return { bg: 'info', statusText: 'Check Released' };
    case 'Reviewed':
      return { bg: 'info', statusText: 'Reviewed' };
    case 'Cancelled':
      return { bg: 'danger', statusText: 'Cancelled' };
    case 'OCRProcessing':
      return { bg: 'warning', statusText: 'OCR Processing' };
    case 'ProductMatching':
      return { bg: 'warning', statusText: 'Matching Products' };
    case 'FailedOCR':
      return { bg: 'warning', statusText: 'Failed OCR' };
    case 'ForDeletion':
      return { bg: 'danger', statusText: 'For Deletion' };
    case 'Sent':
      return { bg: 'info', statusText: 'Sent' };
    case 'Future':
      return { bg: 'secondary', statusText: 'Future' };
    case 'ForConsolidation':
      return { bg: 'warning', statusText: 'For Consolidation' };
    case 'Closed':
      return { bg: 'info', statusText: 'Closed' };
    case 'NotFulfilled':
      return { bg: 'warning', statusText: 'Not Fulfilled' };
    case 'PartiallyFulfilled':
      return { bg: 'primary', statusText: 'Partially Fulfilled' };
    case 'Fulfilled':
      return { bg: 'info', statusText: 'Fulfilled' };
    case 'NoStock':
      return { bg: 'danger', statusText: 'No Stock' };
    default:
      return initialState;
  }
};

const useBadge = (status: string | number | undefined) => {
  const [state] = useReducer(badgeReducer, initialState);
  const action = { type: status };
  const { bg, statusText } = badgeReducer(state, action);
  return { bg, statusText };
};

const BadgeStatus = ({
  status,
  isForDeletion
}: {
  status: string | number | undefined;
  isForDeletion?: boolean;
}) => {
  const { bg, statusText } = useBadge(
    isForDeletion ? 'Delete Requested' : status
  );
  return bg && statusText ? (
    <Badge bg={bg} variant="phoenix" className="fs-10">
      {statusText}
    </Badge>
  ) : null;
};

export default BadgeStatus;
